import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Tabs, 
  Tab, 
  Box, 
  Paper 
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  Add as AddIcon,
  Description as DescriptionIcon,
  Mail as MailIcon,
  History as HistoryIcon,
  MenuBook as MenuBookIcon,
  Euro as EuroIcon,
  Search as SearchIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

const Navigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useAuth();

  // Prüfe, ob wir auf einer Antragdetails-Seite sind (mit ID-Parameter)
  const isOnDetailPage = /^\/zulassungsantraege\/\d+/.test(location.pathname);

  const allTabs = [
    { label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon />, adminOnly: false },
    { label: 'Kunden', path: '/kunden', icon: <BusinessIcon />, adminOnly: true },
    { label: 'Nutzer', path: '/nutzer', icon: <PeopleIcon />, adminOnly: true },
    { label: 'Protokoll', path: '/protokoll', icon: <HistoryIcon />, adminOnly: true },
    { label: 'Dokumentation', path: '/dokumentation', icon: <MenuBookIcon />, adminOnly: true },
    { label: 'Kennzeichensuche', path: '/kennzeichensuche', icon: <SearchIcon />, adminOnly: true },
//    { label: 'Antragstellung', path: '/antragstellung', icon: <AddIcon />, adminOnly: false },
    { label: 'Zulassungsanträge', path: '/zulassungsantraege', icon: <AssignmentIcon />, adminOnly: false },
    { label: 'SABRINA', path: '/sabrina', icon: <MailIcon />, adminOnly: false },
    { label: 'Gebühren', path: '/sabrina/gks02', icon: <EuroIcon />, adminOnly: true },
    { label: 'Einstellungen', path: '/einstellungen', icon: <SettingsIcon />, adminOnly: true },
  ];

  // Füge "Antragdetails" nur hinzu, wenn wir auf einer Detailseite sind
  const tabsWithDetails = isOnDetailPage 
    ? [...allTabs, { label: 'Antragdetails', path: '/zulassungsantraege/:id', icon: <DescriptionIcon />, adminOnly: false, isPattern: true }]
    : allTabs;

  const visibleTabs = tabsWithDetails.filter(t => !t.adminOnly || isAdmin);

  // Aktuelle Route bestimmen gegen sichtbare Tabs
  const getCurrentIndex = (): number | false => {
    // Prüfe zuerst, ob wir auf einer Antragdetails-Seite sind
    if (isOnDetailPage) {
      const detailsTabIndex = visibleTabs.findIndex(t => t.path === '/zulassungsantraege/:id');
      if (detailsTabIndex !== -1) return detailsTabIndex;
    }
    
    // Ansonsten normale Tab-Suche
    const index = visibleTabs.findIndex(t => 
      t.path === location.pathname || (t.path === '/dashboard' && location.pathname === '/')
    );
    // Kein passender Tab gefunden → kein Tab hervorheben (z.B. /profil)
    return index === -1 ? false : index;
  };

  const currentIndex = getCurrentIndex();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    const tab = visibleTabs[newValue];
    if (tab) {
      // Wenn es ein Pattern-Tab ist (wie Antragdetails), gehe zur Übersichtsseite
      if ('isPattern' in tab && tab.isPattern) {
        navigate('/zulassungsantraege');
      } else {
        navigate(tab.path);
      }
    }
  };

  return (
    <Paper elevation={1} sx={{ borderRadius: 0 }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={currentIndex} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 2 }}
        >
          {visibleTabs.map((t) => (
            <Tab 
              key={t.path}
              icon={t.icon}
              label={t.label}
              iconPosition="start"
              sx={{
                minWidth: 120,
                '&.Mui-selected': { color: 'primary.main' },
              }}
            />
          ))}
        </Tabs>
      </Box>
    </Paper>
  );
};

export default Navigation;
